import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Trash2, Edit, Eye, Download } from 'lucide-react';
import { METAL_TYPES, KARATS } from '@/lib/quotation-utils';

export function QuotationClientsTab({
  clientMasters, isAdmin, showClientDialog, setShowClientDialog,
  editingClient, setEditingClient, newClient, setNewClient,
  handleSaveClient, handleDeleteClient, applyClientMaster,
  setSelectedClientMasterId, setActiveTab, handleDownloadMasterRates
}) {
  return (
    <Card>
      <CardHeader className="py-3 flex flex-row items-center justify-between">
        <CardTitle className="text-base">Client Masters (Presets)</CardTitle>
        {isAdmin && (
          <Dialog open={showClientDialog} onOpenChange={setShowClientDialog}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-amber-500 hover:bg-amber-600" data-testid="qt-add-client-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Client
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editingClient ? 'Edit Client' : 'New Client Master'}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Client Name *</Label><Input value={newClient.name} onChange={e => setNewClient({ ...newClient, name: e.target.value })} data-testid="qt-client-dialog-name" /></div>
                  <div><Label>Code *</Label><Input value={newClient.code} onChange={e => setNewClient({ ...newClient, code: e.target.value.toUpperCase() })} placeholder="e.g., DAI01" data-testid="qt-client-dialog-code" /></div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div><Label>Default Metal</Label><Select value={newClient.default_metal} onValueChange={v => setNewClient({ ...newClient, default_metal: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{METAL_TYPES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select></div>
                  <div><Label>Default Karat</Label><Select value={newClient.default_karat} onValueChange={v => setNewClient({ ...newClient, default_karat: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{KARATS.map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent></Select></div>
                  <div><Label>Metal Rate ($/oz)</Label><Input type="number" value={newClient.default_metal_rate} onChange={e => setNewClient({ ...newClient, default_metal_rate: parseFloat(e.target.value) || 0 })} /></div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div><Label>USD-INR Rate</Label><Input type="number" value={newClient.usd_to_inr_rate} onChange={e => setNewClient({ ...newClient, usd_to_inr_rate: parseFloat(e.target.value) || 83.5 })} /></div>
                  <div><Label>Stamping ($)</Label><Input type="number" value={newClient.default_stamping} onChange={e => setNewClient({ ...newClient, default_stamping: parseFloat(e.target.value) || 0 })} /></div>
                  <div><Label>CAD Cost ($)</Label><Input type="number" value={newClient.default_cad_cost} onChange={e => setNewClient({ ...newClient, default_cad_cost: parseFloat(e.target.value) || 0 })} /></div>
                </div>
                <div><Label>Notes</Label><Input value={newClient.notes} onChange={e => setNewClient({ ...newClient, notes: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setShowClientDialog(false); setEditingClient(null); }}>Cancel</Button>
                <Button onClick={handleSaveClient} className="bg-amber-500 hover:bg-amber-600" data-testid="qt-client-dialog-save">{editingClient ? 'Update' : 'Create'}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Metal</TableHead>
              <TableHead>Karat</TableHead><TableHead className="text-right">Metal Rate</TableHead>
              <TableHead className="text-right">USD-INR</TableHead><TableHead>DB Rates</TableHead>
              <TableHead className="text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {clientMasters.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">No client masters. Create one to save presets.</TableCell></TableRow>
            ) : clientMasters.map(c => (
              <TableRow key={c.id} data-testid={`qt-client-row-${c.code}`}>
                <TableCell className="font-mono text-sm">{c.code}</TableCell>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell>{c.default_metal}</TableCell>
                <TableCell>{c.default_karat}K</TableCell>
                <TableCell className="text-right">${c.default_metal_rate}</TableCell>
                <TableCell className="text-right">{'\u20b9'}{c.usd_to_inr_rate}</TableCell>
                <TableCell>{c.has_master_rates ? <span className="text-xs text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded font-medium">Active</span> : <span className="text-xs text-slate-400">-</span>}</TableCell>
                <TableCell className="text-center">
                  <Button variant="ghost" size="sm" onClick={() => { setSelectedClientMasterId(c.id); applyClientMaster(c.id); setActiveTab('calculator'); }} data-testid={`qt-client-apply-${c.code}`}><Eye className="w-4 h-4" /></Button>
                  {c.has_master_rates && <Button variant="ghost" size="sm" className="text-emerald-600" onClick={() => handleDownloadMasterRates(c.code)} data-testid={`qt-client-download-${c.code}`}><Download className="w-4 h-4" /></Button>}
                  {isAdmin && (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => { setEditingClient(c); setNewClient(c); setShowClientDialog(true); }}><Edit className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteClient(c.id)}><Trash2 className="w-4 h-4" /></Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
